import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import { PencilSimpleIcon } from "@/components/icons/pencil-simple-icon";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { EXTRA_FOOD_DEFAULT_MEAL_TIME } from "@/features/meal-plan/constants/extra-food";
import { useAddYourFood } from "@/hooks/requests/your-food/use-add-your-food";
import { useToast } from "@/hooks/ui/use-toast";
import { AddRecipeSchema } from "@/schemas/add-recipe-schema";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter } from "@ionic/react";
import { memo, RefObject, useCallback, useMemo, useState } from "react";
import { SubmitErrorHandler, useForm } from "react-hook-form";
import {
  AddRecipeTextModal,
  DEFAULT_ADD_RECIPE_VALUES,
  type AddRecipeFormValues,
} from "../add-recipe-text-modal";
import { AddFoodModalMode, buildRecipeProcessingRoute } from "./recipe-processing-route";

interface ManualOptionProps {
  pageRef: RefObject<HTMLElement>;
  disabled?: boolean;
  onComplete: () => void;
  mode?: AddFoodModalMode;
  day?: string;
  targetDate?: string;
  mealTime?: Body_add_your_food_api_v1_meals_your_food_post.meal_time;
}

export const ManualOption = memo(function ManualOption({
  pageRef,
  disabled,
  onComplete,
  mode = "default",
  day,
  targetDate,
  mealTime,
}: ManualOptionProps) {
  const router = useIonRouter();
  const { addYourFoodAsync, isLoading } = useAddYourFood();
  const { showError } = useToast();
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const lockedMealTime = useMemo<
    Body_add_your_food_api_v1_meals_your_food_post.meal_time | undefined
  >(() => {
    if (mode === "extra-food") {
      return EXTRA_FOOD_DEFAULT_MEAL_TIME;
    }
    if (mode === "add-to-meal-time") {
      return mealTime;
    }
    return undefined;
  }, [mode, mealTime]);
  const defaultAddRecipeValues = useMemo<AddRecipeFormValues>(() => {
    if (lockedMealTime) {
      return {
        ...DEFAULT_ADD_RECIPE_VALUES,
        mealTime: lockedMealTime,
      };
    }
    return DEFAULT_ADD_RECIPE_VALUES;
  }, [lockedMealTime]);

  const { control, handleSubmit, reset } = useForm<AddRecipeFormValues>({
    defaultValues: defaultAddRecipeValues,
    resolver: zodResolver(AddRecipeSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const openRecipeModal = useCallback(() => {
    reset(defaultAddRecipeValues);
    setIsRecipeModalOpen(true);
  }, [defaultAddRecipeValues, reset]);

  const handleValidRecipeConfirm = useCallback(
    async (values: AddRecipeFormValues) => {
      const submitMealTime =
        lockedMealTime ??
        (values.mealTime as Body_add_your_food_api_v1_meals_your_food_post.meal_time);

      if (!submitMealTime) {
        return;
      }

      try {
        const data = await addYourFoodAsync({
          recipe_name: values.recipeName.trim() || undefined,
          meal_time: submitMealTime,
          portion_size: values.portionSize,
          ingredients: values.ingredients.trim() || undefined,
          instructions: values.instructions.trim() || undefined,
        });

        const mealId = data.meal_id;
        setIsRecipeModalOpen(false);
        reset(defaultAddRecipeValues);
        onComplete();
        router.push(
          buildRecipeProcessingRoute({
            mealId,
            mode,
            day,
            targetDate,
            mealTime: submitMealTime,
          }),
          "forward",
          "replace",
        );
      } catch (error) {
        showError("Failed to add your food. Please try again.");
        console.error("Error adding your food:", error);
      }
    },
    [
      lockedMealTime,
      mode,
      addYourFoodAsync,
      reset,
      defaultAddRecipeValues,
      onComplete,
      router,
      day,
      targetDate,
      showError,
    ],
  );

  const handleInvalidRecipeConfirm = useCallback<SubmitErrorHandler<AddRecipeFormValues>>(
    (errors) => {
      // Keep submit validation feedback in one place and surface it as a toast.
      if (errors.ingredients?.message) {
        showError(errors.ingredients.message);
        return;
      }

      showError("Please review your recipe details and try again.");
    },
    [showError],
  );

  const handleRecipeConfirm = useMemo(
    () => handleSubmit(handleValidRecipeConfirm, handleInvalidRecipeConfirm),
    [handleSubmit, handleValidRecipeConfirm, handleInvalidRecipeConfirm],
  );

  const handleRecipeDismiss = useCallback(() => {
    setIsRecipeModalOpen(false);
    reset(defaultAddRecipeValues);
  }, [defaultAddRecipeValues, reset]);

  return (
    <>
      <OutlinedButton
        color="black"
        textCase="normal"
        disabled={disabled || isLoading}
        onClick={openRecipeModal}
        className="flex items-center justify-between px-3"
      >
        <PencilSimpleIcon className="h-6 w-6" />
        <span className={textVariants.bodySingleLine}>Add manually</span>
        <div className="h-6 w-6" />
      </OutlinedButton>

      <AddRecipeTextModal
        pageRef={pageRef}
        isOpen={isRecipeModalOpen}
        onDismiss={handleRecipeDismiss}
        onConfirm={handleRecipeConfirm}
        control={control}
        fixedMealTime={lockedMealTime}
      />
    </>
  );
});
